import React, { useEffect } from 'react';
import {
    Rocket,
    Target,
    TrendingUp,
    Layers,
    Sparkles,
    BarChart3,
    Calendar,
    Workflow,
    Users,
    Eye,
    Share2,
    Bookmark,
    Heart,
    MessageCircle,
    Link2,
    Send,
    Zap,
    Newspaper,
    Briefcase,
    DollarSign,
    UserCheck,
    Megaphone,
    Flame,
    ArrowUpRight,
    CheckCircle2,
    MousePointerClick,
    PlayCircle
} from 'lucide-react';

/* Reusable Primitives */

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};

const MiniCard = ({ icon: IconComp, title, sub, color = "indigo" }) => {
    const ring = {
        indigo: "text-indigo-300",
        emerald: "text-emerald-300",
        purple: "text-purple-300",
        amber: "text-amber-300",
        rose: "text-rose-300"
    };
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 h-full">
            <IconComp className={`w-6 h-6 mb-3 ${ring[color]}`} />
            <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-snug">{title}</h3>
            <p className="text-slate-400 text-sm leading-snug">{sub}</p>
        </div>
    );
};

const Check = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
        <span className="leading-snug text-slate-200 text-sm">{children}</span>
    </li>
);

const SectionHead = ({ icon, tag, color, title, sub }) => (
    <div className="text-center mb-10">
        <SectionTag icon={icon} label={tag} color={color} />
        <h2 className="text-3xl sm:text-4xl font-black mb-3">{title}</h2>
        {sub && <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">{sub}</p>}
    </div>
);

/* Pillar chip colors for the feed */
const pillarChip = {
    rose: "bg-rose-500/10 text-rose-300 border border-rose-500/20",
    emerald: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-300 border border-amber-500/20",
    purple: "bg-purple-500/10 text-purple-300 border border-purple-500/20",
    indigo: "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20"
};

const FeedCard = ({ pillar, color, hook, kpi, note }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5 flex flex-col h-full">
        <div className="flex items-center justify-between gap-2 mb-3">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${pillarChip[color]}`}>{pillar}</span>
            <span className="text-[10px] font-semibold text-white/40 whitespace-nowrap">{kpi}</span>
        </div>
        <p className="text-white font-semibold text-sm leading-snug mb-3">{hook}</p>
        {note && <p className="text-slate-400 text-xs leading-snug mt-auto">{note}</p>}
    </div>
);

/* 7-day illustrative feed — hooks refreshed with live news each week */
const FEED = [
    {
        day: "Day 1", theme: "Open strong",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Followers · Shares", hook: "Someone built an AI that lives inside your computer, runs your browser and files, and you talk to it through WhatsApp. It is free and open-source.", note: "Wild, scary, true. The lane your WiFi-spy reel won 108K on." },
            { pillar: "AI + Sales", color: "emerald", kpi: "Saves · Leads", hook: "There is an AI that books your sales calls while you sleep, and it replies in your own voice.", note: "Comment a keyword, the bot sends the setup. Captures the lead." },
            { pillar: "Founder", color: "amber", kpi: "Shares · Comments", hook: "I raised 2 million dollars at a 15 million valuation. Then I watched it disappear. Here is what nobody tells you about the highs.", note: "Your real story. Relatability that only you can post." }
        ]
    },
    {
        day: "Day 2", theme: "News + utility",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Followers", hook: "June 2026 is the biggest AI launch month in history, and one model just overtook ChatGPT while running four times faster.", note: "Ends with: follow for the AI news that actually matters, daily." },
            { pillar: "AI + Finance", color: "emerald", kpi: "Saves · Leads", hook: "Stop paying a bookkeeper. This AI already does 98% of a startup's books on its own.", note: "Save-bait. The stop-paying-for-X frame that prints comments." },
            { pillar: "Case Study", color: "indigo", kpi: "Leads", hook: "How we took a real client from X to Y using AI. The exact system, start to finish.", note: "Your proof reel. We plug in a real result you share with us." }
        ]
    },
    {
        day: "Day 3", theme: "Shock + value",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Shares", hook: "An AI agent just ran a full cyberattack on its own. No human touched the keyboard.", note: "Security fear = your most shareable lane." },
            { pillar: "AI + HR", color: "amber", kpi: "Saves · Comments", hook: "This AI now runs your first-round interviews and screens 10x more candidates than any recruiter.", note: "Speaks straight to founders who are hiring." },
            { pillar: "Startup", color: "purple", kpi: "Authority · Shares", hook: "Everyone obsesses over the product. The truth: distribution beats product every single time.", note: "Your philosophy. Builds you as the operator they trust." }
        ]
    },
    {
        day: "Day 4", theme: "Build + sell",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Comments", hook: "Microsoft just made a tool where you describe an app in plain English and it writes the entire code.", note: "Completeness frame: it builds the whole thing, not just a part." },
            { pillar: "AI + Marketing", color: "purple", kpi: "Saves · Leads", hook: "A five-person business can now make ads that used to need a full agency. Here is the exact tool.", note: "Tool drop + comment-to-DM. Built to capture marketers." },
            { pillar: "Founder", color: "amber", kpi: "Shares", hook: "I launch everything at 70% clarity. Waiting for 100% is why most founders never ship.", note: "The P70 mindset. Quotable, save-worthy, you." }
        ]
    },
    {
        day: "Day 5", theme: "Reach + intent",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Followers · Link clicks", hook: "Google just turned search into a conversation. If you run a business, your traffic is about to change.", note: "Ties to your real authority: distribution and SEO." },
            { pillar: "AI + Sales", color: "emerald", kpi: "Leads · Saves", hook: "This AI finds who is ready to buy right now and writes the message before you even log in.", note: "High-intent. Routes the viewer into a conversation." },
            { pillar: "Case Study", color: "indigo", kpi: "Leads", hook: "The exact AI system we built that booked real calls in 30 days.", note: "Your second proof reel, the strongest lead driver on the feed." }
        ]
    },
    {
        day: "Day 6", theme: "Cheap + smart",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Shares", hook: "Someone trained a 100-billion-parameter AI for 1.25 dollars an hour. AI just got absurdly cheap.", note: "Ownership angle — the cost wall is gone." },
            { pillar: "AI + Finance", color: "emerald", kpi: "Saves", hook: "An AI CFO that shows your real runway in real time. Founders are quietly replacing their finance guy.", note: "Reference content founders bookmark and act on." },
            { pillar: "Startup", color: "purple", kpi: "Comments", hook: "B2B is easier, simpler, and more profitable than B2C. I learned it the hard way.", note: "Contrarian + your scars. Drives debate in the comments." }
        ]
    },
    {
        day: "Day 7", theme: "Big finish",
        reels: [
            { pillar: "AI News", color: "rose", kpi: "Shares · Followers", hook: "The robots are here. NVIDIA just dropped an AI that sees, reasons, and acts in the real world.", note: "Future-shock. The crazy-story format that broke 400K for you." },
            { pillar: "AI + Marketing", color: "purple", kpi: "Comments", hook: "Should you fire your marketing agency? This AI does 80% of what they charge you for.", note: "Spicy question = a comment section that fuels reach." },
            { pillar: "Founder", color: "amber", kpi: "Shares", hook: "Radical acceptance: people leave, processes stay. That one shift saved my company.", note: "Closes the week on your hardest-won lesson." }
        ]
    }
];

/* KPI to mechanism */
const KPIS = [
    { icon: Users, color: "indigo", kpi: "Followers", how: "Every reel ends with a reason to follow, not a dead-end. A named, recurring series people subscribe to." },
    { icon: Share2, color: "rose", kpi: "Shares per post", how: "Utility and shock content built to forward. “Send this to your team” beats “like this.”" },
    { icon: Bookmark, color: "emerald", kpi: "Saves per post", how: "Reference reels: tool stacks, prompts, step-by-steps. Made to be saved and used later." },
    { icon: Heart, color: "purple", kpi: "Likes & comments", how: "Comment-keyword CTAs, contrarian takes, and we reply to every comment in the first hour." },
    { icon: Eye, color: "amber", kpi: "Story views", how: "A daily story rhythm that trains your audience to check in, not a once-a-week burst." },
    { icon: ArrowUpRight, color: "indigo", kpi: "Post to profile", how: "Curiosity gaps and Part-2 hooks that pull viewers off the reel and onto your profile." },
    { icon: MousePointerClick, color: "emerald", kpi: "Story to profile", how: "Story CTAs and link stickers that route warm viewers straight to your latest." },
    { icon: Link2, color: "rose", kpi: "Link clicks", how: "Automated comment-to-DM links plus a clean link-in-bio funnel. The path from view to lead." }
];

/* The new pillar mix */
const PILLARS = [
    { icon: Newspaper, color: "rose", title: "AI News", share: "Reach engine", sub: "The wild, stop-the-scroll stories that win you new viewers, now ending with a follow hook." },
    { icon: Briefcase, color: "emerald", title: "AI + Sales", share: "Lead pillar", sub: "AI that books meetings and works your pipeline. Speaks straight to buyers." },
    { icon: Megaphone, color: "purple", title: "AI + Marketing", share: "Lead pillar", sub: "AI that makes ads and content. The tools your audience wants today." },
    { icon: UserCheck, color: "amber", title: "AI + HR", share: "Value pillar", sub: "AI hiring and screening. Saves for every founder who is staffing up." },
    { icon: DollarSign, color: "emerald", title: "AI + Finance", share: "Value pillar", sub: "AI bookkeeping and CFO tools. Practical, save-worthy, founder-relevant." },
    { icon: Flame, color: "amber", title: "Startup + Founder", share: "Authority", sub: "Your story and your philosophy. The content only you can post." },
    { icon: BarChart3, color: "indigo", title: "Case Studies", share: "Proof = leads", sub: "The real AI work we deliver. The single strongest lead driver on the feed." }
];

/* Daily stories plan */
const STORIES = [
    { icon: MessageCircle, color: "amber", title: "Morning poll or question", sub: "“Would you trust an AI to do this?” Opens engagement and counts as a story view." },
    { icon: PlayCircle, color: "indigo", title: "Behind today's reel", sub: "A peek + “new drop, tap through.” Drives story-to-profile and post-to-profile." },
    { icon: Zap, color: "emerald", title: "One AI quick-tip", sub: "A single useful card. Builds the daily check-in habit." },
    { icon: Link2, color: "rose", title: "Link / keyword reminder", sub: "“Comment the word, or tap the link.” The story-side lead capture." },
    { icon: Heart, color: "purple", title: "Re-share a reply or win", sub: "Social proof from your own community. Pulls people to your profile." }
];

/* Main Component */
export default function KushGrowthPlan() {

    useEffect(() => {
        document.title = "RKD | 2-Month Growth Plan";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">RKD Growth Plan</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">2-Month Plan</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-20 sm:space-y-28">

                {/* HERO */}
                <section className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Rocket} label="The 60-Day Growth Sprint" />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        Big numbers don't come from luck.<br />They come from a system.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-10">
                        You set the target: serious growth in the next 60 days. Here is exactly how we attack every metric you asked for, the content mix that gets us there, and a real 7-day feed so you can see it.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-3 sm:gap-4 mt-10">
                        <StatCard value="8" label="KPIs we move" sub="the scoreboard you set" accent="indigo" />
                        <StatCard value="7" label="Content pillars" sub="news → business → founder" accent="rose" />
                        <StatCard value="Daily" label="Publishing rhythm" sub="reels + stories, every day" accent="emerald" />
                        <StatCard value="Leads" label="The real goal" sub="reach that converts to AI clients" accent="purple" />
                    </div>
                </section>

                {/* WE HEARD YOU — THE KPIs */}
                <section>
                    <SectionHead
                        icon={Target} tag="The scoreboard" color="indigo"
                        title="Every metric you asked for"
                        sub="These are the numbers we are building the whole engine around. Nothing on this page is here for decoration."
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto">
                        {[
                            { i: Users, l: "Followers" },
                            { i: Share2, l: "Shares" },
                            { i: Bookmark, l: "Saves" },
                            { i: Heart, l: "Engagement" },
                            { i: Eye, l: "Story views" },
                            { i: ArrowUpRight, l: "Post → profile" },
                            { i: MousePointerClick, l: "Story → profile" },
                            { i: Link2, l: "Link clicks" }
                        ].map((k, idx) => {
                            const Icon = k.i;
                            return (
                                <div key={idx} className="rounded-xl border border-white/10 bg-white/5 px-3 py-4 flex flex-col items-center text-center gap-2">
                                    <Icon className="w-5 h-5 text-indigo-300" />
                                    <span className="text-white text-xs sm:text-sm font-semibold">{k.l}</span>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* THE NEW CONTENT MIX */}
                <section>
                    <SectionHead
                        icon={Layers} tag="The content mix" color="rose"
                        title="Your feed becomes a growth machine"
                        sub="We keep the wild AI news that wins you reach, and aim it at the audience that follows you and becomes a client: founders and operators."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        {PILLARS.map((p, idx) => (
                            <div key={idx} className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 h-full">
                                <div className="flex items-center justify-between mb-3">
                                    <p.icon className={`w-6 h-6 ${pillarChip[p.color].split(' ')[1]}`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${pillarChip[p.color]}`}>{p.share}</span>
                                </div>
                                <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-snug">{p.title}</h3>
                                <p className="text-slate-400 text-sm leading-snug">{p.sub}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* KPI -> MECHANISM */}
                <section>
                    <SectionHead
                        icon={Workflow} tag="How each number moves" color="emerald"
                        title="A lever for every metric"
                        sub="Followers, shares, saves, clicks. Each one has a specific mechanism built into the content. Not hope. A method."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-5xl mx-auto">
                        {KPIS.map((k, idx) => (
                            <div key={idx} className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 flex gap-4 items-start h-full">
                                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${pillarChip[k.color]}`}>
                                    <k.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-sm sm:text-base mb-1">{k.kpi}</h3>
                                    <p className="text-slate-400 text-sm leading-snug">{k.how}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* THE LEAD ENGINE */}
                <section>
                    <SectionHead
                        icon={Zap} tag="Reach → revenue" color="amber"
                        title="Turning your views into AI leads"
                        sub="The whole point: your account should generate business, not just numbers. Here is the bridge from a viewer to a conversation."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={Send} color="amber" title="Automated DM replies" sub="A viewer comments a keyword, an automation instantly sends the link or booking, and replies in your tone, around the clock." />
                        <MiniCard icon={Link2} color="amber" title="Link-in-bio funnel" sub="A clean path from profile to a single action: book a call or grab the resource. Every high-intent reel points here." />
                        <MiniCard icon={Briefcase} color="amber" title="Built for AI buyers" sub="The sales, marketing, and case-study pillars attract people with budget and a problem you solve." />
                    </div>
                    <p className="text-slate-500 text-xs text-center max-w-3xl mx-auto mt-5">The lead automation is rebuilt to run on Instagram's official tools, so it scales without putting your account at risk.</p>
                </section>

                {/* THE 7-DAY FEED */}
                <section>
                    <SectionHead
                        icon={Calendar} tag="See it live" color="indigo"
                        title="What your next 7 days look like"
                        sub="Real ideas, built on this week's actual AI news, mapped to the pillars and the exact KPI each one is built to move. This is the feed, not a theory."
                    />
                    <div className="max-w-5xl mx-auto space-y-7">
                        {FEED.map((day) => (
                            <div key={day.day}>
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-white font-black text-lg sm:text-xl">{day.day}</span>
                                    <span className="text-white/40 text-[10px] sm:text-xs uppercase tracking-widest">{day.theme}</span>
                                    <div className="flex-grow h-px bg-white/10" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {day.reels.map((r, i) => (
                                        <FeedCard key={i} {...r} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-slate-500 text-xs text-center max-w-3xl mx-auto mt-6">Illustrative week. Hooks are refreshed against the live news cycle before each batch, so the feed always rides what is breaking right now.</p>
                </section>

                {/* DAILY STORIES PLAN */}
                <section>
                    <SectionHead
                        icon={Eye} tag="The stories plan" color="purple"
                        title="What to post on stories, every day"
                        sub="Stories are where four of your KPIs live: story views, story-to-profile, post-to-profile, and link clicks. Here is the daily set."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        {STORIES.map((s, idx) => (
                            <MiniCard key={idx} icon={s.icon} color={s.color} title={s.title} sub={s.sub} />
                        ))}
                    </div>
                    <div className="max-w-3xl mx-auto mt-6 rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6 text-center">
                        <p className="text-slate-200 text-sm leading-snug">
                            <span className="text-white font-bold">The daily rhythm:</span> a consistent run of reels across the seven pillars, plus 4 to 5 stories every day. Consistency is what turns a spike into a curve.
                        </p>
                    </div>
                </section>

                {/* WHAT WE NEED FROM YOU */}
                <section>
                    <SectionHead
                        icon={Sparkles} tag="To go all-in" color="emerald"
                        title="Two things only you can give us"
                        sub="The engine runs on our side. These two unlock the highest-value lanes."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                        <MiniCard icon={BarChart3} color="emerald" title="Your case studies" sub="The real AI results you have delivered. This is the strongest lead-driving pillar, and only you have the numbers." />
                        <MiniCard icon={Briefcase} color="emerald" title="The business topics sheet" sub="The startup and business angles you want to own, so we weave your voice into every batch." />
                    </div>
                </section>

                {/* CLOSING */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 sm:p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-9 h-9 sm:w-10 sm:h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 text-white">Let's make the next 60 days the ones that change the trajectory.</h2>
                        <p className="text-slate-300 text-base sm:text-lg mb-2">The system is ready. The feed is mapped. Let's run it.</p>
                        <p className="text-slate-400 text-sm">Suresh Malani · SocialSEO LLP</p>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-8 border-t border-white/10">
                    <div className="font-bold text-white/70 mb-2">SocialSEO LLP</div>
                    <div className="mb-1">Delhi + Dubai</div>
                    <div className="mb-2">
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
